<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="hand-holding-usd" /> Nạp hạn mức cho khách của đối tác</h1>
      </div>
      <div v-if="canCreate">
        <button class="btn btn-success" @click="openCreate">
          <font-awesome-icon icon="plus" /> Tạo lệnh nạp hạn mức
        </button>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <ul class="nav nav-tabs">
          <li class="nav-item" v-for="tab in visibleStatusTabs" :key="tab.value">
            <a class="nav-link" href="#" :class="{ active: filterFormModel.status === tab.value }"
               @click.prevent="changeStatus(tab.value)">{{ tab.label }}</a>
          </li>
        </ul>
      </div>

      <div class="col-12">
        <div class="tile">
          <div class="c-filter-row d-flex flex-wrap align-items-center mb-2" style="gap:8px">
            <admin-user-select v-if="!isAgency" style="min-width:220px"
                               :model="filterFormModel" attribute="agency" roleFilter="agency"
                               place-holder="Lọc theo đối tác"></admin-user-select>
            <client-select :model="filterFormModel" attribute="client" @change="doSearch" />
            <button @click="doSearch" class="btn btn-primary">Tìm kiếm</button>
            <button @click="reset" class="btn btn-secondary">Reset</button>
          </div>

          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th v-if="!isAgency">Đối tác</th>
                  <th>Khách hàng</th>
                  <th>Domain</th>
                  <th class="text-right">Hạn mức nạp</th>
                  <th>Diễn giải</th>
                  <th>Người gửi</th>
                  <th>Ngày</th>
                  <th>Trạng thái</th>
                  <th v-if="hasActions" class="text-right">Hành động</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!items.length">
                  <td :colspan="columnCount" class="text-center text-muted py-3">Không có lệnh nào</td>
                </tr>
                <tr v-for="item in items" :key="item._id">
                  <td v-if="!isAgency">{{ item.agency ? item.agency.name : '-' }}</td>
                  <td>
                    <div><b>{{ item.client ? (item.client.companyName || item.client.domain) : '-' }}</b></div>
                    <div class="small text-muted" v-if="item.status === 'approved' && item.creditLimitAfter != null">
                      Hạn mức: {{ fmt(item.creditLimitBefore) }} → <b>{{ fmt(item.creditLimitAfter) }}</b>
                    </div>
                  </td>
                  <td class="small">{{ item.client && item.client.domain ? item.client.domain : '-' }}</td>
                  <td class="text-right text-success"><b>+{{ fmt(item.amount) }}</b></td>
                  <td class="small">{{ item.note || '-' }}</td>
                  <td class="small">{{ item.createdBy ? item.createdBy.name : '-' }}</td>
                  <td class="small">{{ formatTime(item.createTime) }}</td>
                  <td>
                    <span class="badge" :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
                    <div class="small text-muted" v-if="item.status === 'approved' && item.approvedBy">Duyệt: {{ item.approvedBy.name }}</div>
                    <div class="small text-danger" v-if="item.status === 'rejected'">
                      <div v-if="item.rejectedBy">Từ chối bởi: {{ item.rejectedBy.name }}</div>
                      <div v-if="item.rejectReason" :title="item.rejectReason">Lý do: {{ item.rejectReason }}</div>
                    </div>
                  </td>
                  <td v-if="hasActions" class="text-right" style="white-space:nowrap">
                    <template v-if="item.status === 'pending' && canProcess">
                      <button class="btn btn-sm btn-success mr-1" @click="approve(item)">Duyệt</button>
                      <button class="btn btn-sm btn-danger" @click="openReject(item)">Từ chối</button>
                    </template>
                    <button v-if="item.status === 'pending' && isOwner(item)" class="btn btn-sm btn-outline-secondary is-border-1 mr-1"
                            @click="recall(item)" title="Thu hồi về Nháp để sửa">
                      <font-awesome-icon icon="undo" /> Thu hồi
                    </button>
                    <template v-if="item.status === 'draft' && isOwner(item)">
                      <button class="btn btn-sm btn-primary mr-1" @click="openEdit(item)">Sửa</button>
                      <button class="btn btn-sm btn-success mr-1" @click="submitDraft(item)">Gửi lại</button>
                    </template>
                    <button v-if="['rejected', 'draft'].includes(item.status) && canDelete(item)" class="btn btn-sm btn-outline-danger is-border-1"
                            @click="remove(item)" title="Xoá lệnh">
                      <font-awesome-icon icon="trash" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="c-paging clearfix mt-2">
            <div class="c-paging__count" v-if="itemCount">Tìm thấy {{ itemCount }} lệnh</div>
            <div class="c-paging__right">
              <div class="c-paging__numbers">
                <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right mb-0" size="sm"
                              v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                              @input="load"></b-pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal tạo lệnh -->
    <b-modal v-model="createVisible" :title="editingId ? 'Sửa lệnh nạp hạn mức (Nháp)' : 'Tạo lệnh nạp hạn mức'" hide-footer centered no-close-on-backdrop>
      <div class="alert alert-warning small">
        <font-awesome-icon icon="exclamation-triangle" />
        <b> Cảnh báo trách nhiệm:</b> Lệnh này khi được Callio (Account / Kế toán) duyệt sẽ cộng vào
        <b>HẠN MỨC</b> (không phải số dư) của khách hàng — khách được dùng dịch vụ ghi nợ tới hạn mức.
        Bạn (đối tác) <b>chịu trách nhiệm thanh toán công nợ</b> phát sinh với Callio cho phần hạn mức đã cấp.
      </div>
      <div class="form-group" v-if="!editingId">
        <label>Khách hàng <span class="text-danger">*</span></label>
        <client-select :model="createForm" attribute="client" />
      </div>
      <div class="form-group">
        <label>Số tiền hạn mức nạp (đ) <span class="text-danger">*</span></label>
        <input type="number" min="1" class="form-control c-placeholder-light" v-model.number="createForm.amount" placeholder="VD: 5000000" />
        <small class="text-muted" v-if="createForm.amount > 0">= {{ fmt(createForm.amount) }} đ</small>
      </div>
      <div class="form-group">
        <label>Diễn giải</label>
        <textarea class="form-control c-placeholder-light" rows="2" v-model="createForm.note" maxlength="1000"
                  placeholder="VD: Khách đã chuyển khoản 5.000.000đ ngày..."></textarea>
      </div>
      <div class="text-right">
        <button class="btn btn-secondary mr-2" @click="createVisible = false">Huỷ</button>
        <button class="btn btn-success" :disabled="acting || (!editingId && !createForm.client) || !(createForm.amount > 0)" @click="doCreate">
          {{ editingId ? 'Lưu nháp' : 'Gửi lệnh' }}
        </button>
      </div>
    </b-modal>

    <!-- Modal từ chối -->
    <b-modal v-model="rejectVisible" title="Từ chối lệnh nạp hạn mức" hide-footer centered>
      <div class="form-group">
        <label>Lý do từ chối <span class="text-danger">*</span></label>
        <textarea class="form-control" rows="3" v-model="rejectReason" maxlength="500"
                  placeholder="Nhập lý do để đối tác biết"></textarea>
      </div>
      <div class="text-right">
        <button class="btn btn-secondary mr-2" @click="rejectVisible = false">Huỷ</button>
        <button class="btn btn-danger" :disabled="acting || !rejectReason.trim()" @click="doReject">Xác nhận từ chối</button>
      </div>
    </b-modal>
  </main>
</template>

<script>
import moment from 'moment';
import List from '@/mixins/List';
import ClientSelect from '@/components/ClientSelect.vue';
import AdminUserSelect from '@/components/AdminUserSelect';

export default {
  mixins: [List],
  components: { ClientSelect, AdminUserSelect },
  data() {
    return {
      service: '/partner-credit',
      autoLoad: true,
      defaultFilterFormModel: { page: 1, pageSize: 50, status: 'pending', client: null, agency: null },
      statusTabs: [
        { value: 'draft', label: 'Nháp', agencyOnly: true },
        { value: 'pending', label: 'Chờ duyệt' },
        { value: 'approved', label: 'Đã duyệt' },
        { value: 'rejected', label: 'Đã từ chối' },
      ],
      createVisible: false,
      editingId: null,
      createForm: { client: null, amount: null, note: '' },
      rejectVisible: false,
      rejectReason: '',
      rejectTarget: null,
      acting: false,
    };
  },
  computed: {
    role() { return this.$store.state.user.role; },
    isAgency() { return this.role === 'agency'; },
    canCreate() { return this.role === 'agency'; },
    canProcess() { return ['owner', 'admin', 'account', 'accountant'].includes(this.role); },
    visibleStatusTabs() { return this.statusTabs.filter(t => !t.agencyOnly || this.isAgency); },
    // Cột Hành động chỉ hiện khi tab hiện tại có hành động (ẩn ở tab Đã duyệt) (1.3)
    hasActions() {
      const s = this.filterFormModel.status;
      if (s === 'pending') return this.canProcess || this.isAgency;
      if (s === 'draft') return this.isAgency;
      if (s === 'rejected') return this.isAgency || ['owner', 'admin'].includes(this.role);
      return false;
    },
    columnCount() {
      let n = 7; // Khách hàng, Domain, Hạn mức, Diễn giải, Người gửi, Ngày, Trạng thái
      if (!this.isAgency) n += 1; // Đối tác
      if (this.hasActions) n += 1; // Hành động
      return n;
    },
  },
  methods: {
    changeStatus(status) {
      this.filterFormModel.status = status;
      this.filterFormModel.page = 1;
      this.load();
    },
    doSearch() {
      this.filterFormModel.page = 1;
      this.load();
    },
    isOwner(item) {
      return this.isAgency && item.agency && (item.agency._id === this.$store.state.user._id || item.agency === this.$store.state.user._id);
    },
    fmt(n) { return (n != null) ? Number(n).toLocaleString('vi-VN') : '0'; },
    formatTime(t) { return t ? moment(t).format('DD/MM/YYYY HH:mm') : '-'; },
    statusLabel(s) { return { pending: 'Chờ duyệt', approved: 'Đã duyệt', rejected: 'Đã từ chối' }[s] || s; },
    statusClass(s) { return { pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-danger' }[s] || 'badge-secondary'; },
    canDelete(item) {
      if (['owner', 'admin'].includes(this.role)) return true;
      return this.isAgency && item.agency && (item.agency._id === this.$store.state.user._id || item.agency === this.$store.state.user._id);
    },
    openCreate() {
      this.editingId = null;
      this.createForm = { client: null, amount: null, note: '' };
      this.createVisible = true;
    },
    openEdit(item) {
      this.editingId = item._id;
      this.createForm = { client: item.client ? item.client._id : null, amount: item.amount, note: item.note || '' };
      this.createVisible = true;
    },
    doCreate() {
      if (!(this.createForm.amount > 0)) return;
      if (!this.editingId && !this.createForm.client) return;
      this.acting = true;
      const req = this.editingId
        ? this.$service.put(`${this.service}/${this.editingId}`, { amount: this.createForm.amount, note: this.createForm.note })
        : this.$service.post(this.service, { client: this.createForm.client, amount: this.createForm.amount, note: this.createForm.note });
      req.then(() => {
        this.createVisible = false;
        const text = this.editingId ? 'Đã lưu nháp.' : 'Chờ Callio duyệt.';
        this.$swal({ title: this.editingId ? 'Đã lưu' : 'Đã gửi lệnh', text, icon: 'success', toast: true, position: 'top-end', timer: 3000 });
        this.load();
      }).catch(error => {
        const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
        this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
      }).finally(() => { this.acting = false; });
    },
    recall(item) {
      this.$swal({
        title: 'Thu hồi lệnh về Nháp?',
        html: 'Lệnh sẽ chuyển về <b>Nháp</b> để bạn chỉnh sửa. Callio sẽ không duyệt lệnh khi đang ở Nháp.',
        icon: 'question', showCancelButton: true, confirmButtonText: 'Thu hồi', cancelButtonText: 'Huỷ',
      }).then(result => {
        if (!result.value) return;
        this.$service.post(`${this.service}/${item._id}/recall`).then(() => {
          this.$swal({ title: 'Đã thu hồi về Nháp', icon: 'success', toast: true, position: 'top-end', timer: 2500 });
          this.changeStatus('draft');
        }).catch(error => {
          const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
          this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
        });
      });
    },
    submitDraft(item) {
      this.$swal({
        title: 'Gửi lại lệnh để chờ duyệt?',
        html: `Nạp hạn mức <b>${this.fmt(item.amount)}đ</b> cho <b>${item.client ? (item.client.companyName || item.client.domain) : ''}</b>.`,
        icon: 'question', showCancelButton: true, confirmButtonText: 'Gửi lại', cancelButtonText: 'Huỷ',
      }).then(result => {
        if (!result.value) return;
        this.$service.post(`${this.service}/${item._id}/submit`).then(() => {
          this.$swal({ title: 'Đã gửi lại', icon: 'success', toast: true, position: 'top-end', timer: 2500 });
          this.changeStatus('pending');
        }).catch(error => {
          const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
          this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
        });
      });
    },
    approve(item) {
      this.$swal({
        title: 'Duyệt lệnh nạp hạn mức?',
        html: `Sẽ nạp hạn mức <b>${this.fmt(item.amount)}đ</b> cho <b>${item.client ? (item.client.companyName || item.client.domain) : ''}</b> (hạn mức giảm về phía âm).`,
        icon: 'question', showCancelButton: true, confirmButtonText: 'Duyệt', cancelButtonText: 'Huỷ',
      }).then(result => {
        if (!result.value) return;
        const loader = this.$loading.show();
        this.$service.post(`${this.service}/${item._id}/approve`).then(() => {
          this.$swal({ title: 'Đã duyệt', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
          this.load();
        }).catch(error => {
          const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
          this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
        }).finally(() => loader.hide());
      });
    },
    openReject(item) {
      this.rejectTarget = item;
      this.rejectReason = '';
      this.rejectVisible = true;
    },
    doReject() {
      this.acting = true;
      this.$service.post(`${this.service}/${this.rejectTarget._id}/reject`, { reason: this.rejectReason }).then(() => {
        this.rejectVisible = false;
        this.$swal({ title: 'Đã từ chối', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
        this.load();
      }).catch(error => {
        const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
        this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
      }).finally(() => { this.acting = false; });
    },
    remove(item) {
      this.$swal({
        title: 'Xoá lệnh đã từ chối?', icon: 'warning', showCancelButton: true,
        confirmButtonText: 'Xoá', cancelButtonText: 'Huỷ',
      }).then(result => {
        if (!result.value) return;
        this.$service.delete(`${this.service}/${item._id}`).then(() => {
          this.$swal({ title: 'Đã xoá', icon: 'success', toast: true, position: 'top-end', timer: 2000 });
          this.load();
        }).catch(error => {
          const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
          this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
        });
      });
    },
  },
};
</script>

<style scoped>
/* Căn hàng filter: bỏ margin-bottom mặc định của b-form-group trong select → thẳng hàng với nút Tìm kiếm/Reset */
.c-filter-row >>> .form-group { margin-bottom: 0; }
/* 1.5.1 — placeholder xám nhạt để không nhầm là đã nhập dữ liệu */
.c-placeholder-light::placeholder { color: #b0b7c3; opacity: 1; }
.c-placeholder-light::-webkit-input-placeholder { color: #b0b7c3; }
.c-placeholder-light::-moz-placeholder { color: #b0b7c3; opacity: 1; }
</style>
